import React from 'react';
import { Box, ListItem, Typography } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';
import { Explore, LocationOnRounded } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { unpackToTime } from '@campfire/hot-date';
import { TitularTooltip } from '@campfire/titular-tooltip';
import { MyElementsMyActivityType, ActivitySession } from './my-elements-my-activity-types';
import { MyActivityRosterings } from '../../__generated__/MyActivityRosterings';
import { DateInfoCard } from '../../../../../../common/cards/date-info-card/DateInfoCard';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';
import { getDisplayTimeSchedule } from '../../../../../../common/functions/activity-display-helpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    footerWrapper: {
      display: (props: { isMobile: boolean; isMd: boolean }) => (props.isMobile || props.isMd ? 'auto' : 'flex'),
      alignItems: 'center',
      marginTop: (props: { isMobile: boolean; isMd: boolean }) => (props.isMobile ? '16px' : '40px'),
    },
    footerItem: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: '24px',
      paddingTop: (props: { isMobile: boolean; isMd: boolean }) =>
        props.isMobile ? '4px' : props.isMd ? '8px' : '0px',
    },
    footerItemIcon: {
      color: theme.color.grey.neutral200,
      fontSize: 14,
      marginRight: '6px',
    },
    footerItemLargeNumber: {
      fontWeight: 600,
      color: theme.color.grey.neutral200,
      fontSize: 14,
      marginRight: '6px',
    },
    footerItemText: {
      color: theme.color.grey.neutral200,
      marginTop: '1px',
    },
  })
);

interface Props {
  myActivity: MyElementsMyActivityType;
  onClick: () => void;
  color: {
    primary: string;
    secondary: string;
  };
  actionMenu: React.ReactNode;
  rostering?: MyActivityRosterings;
  isSelected: boolean;
}

export const MyElementsMyActivityListItem = (props: Props) => {
  const {
    myActivity: {
      activityDate,
      activityName,
      startTime,
      endTime,
      activityLocation,
      numSessions,
      programName,
      sessions,
    },
    onClick,
    color,
    actionMenu,
    rostering,
    isSelected,
  } = props;

  const { isMobile, isMd, theme } = useCampfireTheme();
  const classes = useStyles({ isMobile, isMd });
  const formatTime = (time: string) => unpackToTime(time).toFormat('h:mm a');

  const renderActivityTime = (
    activityStartTime: string,
    activityEndTime: string,
    activitySessions?: ActivitySession[],
    dateForActivity?: string
  ) => {
    // Check if user is rostered for any sessions
    const rosteredSessionIds = rostering?.sessionRosterings.map((sr) => sr.session.sessionId) || [];

    // Find all rostered sessions
    const rosteredSessions =
      activitySessions?.filter((session) => rosteredSessionIds.includes(session.sessionId)) || [];

    // If we have rostered sessions, calculate the latest end time
    let latestEndTime = '';
    let earliestStartTime = '';

    // First check if we have sessions with flexible instances for the current date
    if (activitySessions && dateForActivity) {
      // Look for flexible sessions with instances for this date
      const flexibleSessionsWithInstances = activitySessions.filter(
        (session) =>
          session.isFlexible &&
          session.currentFlexibleInstances &&
          session.currentFlexibleInstances.length > 0 &&
          session.currentFlexibleInstances[0] &&
          (!session.currentFlexibleInstances[0].sessionDate ||
            session.currentFlexibleInstances[0].sessionDate === dateForActivity)
      );

      if (flexibleSessionsWithInstances.length > 0) {
        // For rostered sessions, track the earliest start and latest end time
        if (rosteredSessions.length > 0) {
          // Get the earliest start time and latest end time from all rostered sessions
          rosteredSessions.forEach((session) => {
            // For flexible sessions, use the instance time
            if (session.isFlexible && session.currentFlexibleInstances && session.currentFlexibleInstances.length > 0) {
              const instance = session.currentFlexibleInstances[0];
              if (instance.startTime && (!earliestStartTime || instance.startTime < earliestStartTime)) {
                earliestStartTime = instance.startTime;
              }
              if (instance.endTime && (!latestEndTime || instance.endTime > latestEndTime)) {
                latestEndTime = instance.endTime;
              }
            }
            // For regular sessions, use the session time
            else {
              if (session.startTime && (!earliestStartTime || session.startTime < earliestStartTime)) {
                earliestStartTime = session.startTime;
              }
              if (session.endTime && (!latestEndTime || session.endTime > latestEndTime)) {
                latestEndTime = session.endTime;
              }
            }
          });

          // If we have calculated valid times, return the time range
          if (earliestStartTime && latestEndTime) {
            return `${formatTime(earliestStartTime)} - ${formatTime(latestEndTime)}`;
          }
        }

        // If no rostered sessions or couldn't calculate times, show flexible session times
        const flexibleTimes = flexibleSessionsWithInstances
          .map((session) => {
            if (!session.currentFlexibleInstances || !session.currentFlexibleInstances.length) {
              return null;
            }
            const flexibleInstance = session.currentFlexibleInstances[0];
            if (flexibleInstance && flexibleInstance.startTime && flexibleInstance.endTime) {
              const timeStr = getDisplayTimeSchedule(
                flexibleInstance.startTime,
                flexibleInstance.endTime,
                true,
                flexibleInstance
              );
              // If we have multiple sessions, prefix with session name
              const displayName = flexibleInstance.name || session.name;
              return flexibleSessionsWithInstances.length > 1 ? `${displayName}: ${timeStr}` : timeStr;
            }
            return null;
          })
          .filter(Boolean);

        if (flexibleTimes.length > 0) {
          return flexibleTimes.join(', ');
        }
      }
    }

    // Default to regular activity time
    return activityStartTime && activityEndTime
      ? `${formatTime(activityStartTime)} - ${formatTime(activityEndTime)}`
      : '';
  };

  return (
    <ListItem
      onClick={onClick}
      style={{
        cursor: 'pointer',
        paddingLeft: '0',
        paddingRight: '0',
        paddingTop: isMobile ? '4px' : '8px',
        paddingBottom: isMobile ? '4px' : '8px',
      }}
    >
      <DateInfoCard date={activityDate} color={color} selected={isSelected} size='l'>
        <Box display='flex' flexDirection='column' width='100%'>
          <Box display='flex' justifyContent='space-between' alignSelf='stretch'>
            <TitularTooltip title={activityName}>
              <Typography
                variant={'h6'}
                noWrap={!isMobile}
                style={
                  isMobile
                    ? {
                        color: theme.color.grey.neutral400,
                        fontWeight: 800,
                        width: '100%',
                        letterSpacing: '-0.02em',
                        overflow: 'hidden',
                        maxHeight: '3.5em',
                        textOverflow: 'ellipsis',
                      }
                    : { color: theme.color.grey.neutral400, fontWeight: 800, width: '100%', letterSpacing: '-0.02em' }
                }
              >
                {activityName}
              </Typography>
            </TitularTooltip>
            {actionMenu}
          </Box>
          {rostering ? (
            <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
              Attending:{' '}
              {rostering.sessionRosterings
                .map((sessionRostering) => {
                  // Check if there's a matching session with flexible instances
                  if (sessions && sessions.length > 0) {
                    const { sessionId } = sessionRostering.session;
                    const matchingSession = sessions.find((session) => session.sessionId === sessionId);

                    // If we have a matching session with flexible instances
                    if (
                      matchingSession &&
                      matchingSession.isFlexible &&
                      matchingSession.currentFlexibleInstances &&
                      matchingSession.currentFlexibleInstances.length > 0
                    ) {
                      // Get the first flexible instance (which should be for the current date)
                      const flexibleInstance = matchingSession.currentFlexibleInstances[0];

                      // Use the instance name if available, or fall back to the session name
                      // We need to check if the name is available on the instance or somewhere else
                      if (flexibleInstance.name) {
                        return flexibleInstance.name;
                      }
                    }
                  }
                  // Default to the original session name
                  return sessionRostering.session.name;
                })
                .join(', ')}
            </Typography>
          ) : null}
          <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200 }}>
            {renderActivityTime(startTime, endTime, sessions, activityDate)}
          </Typography>
          <Box className={classes.footerWrapper}>
            <Box className={classes.footerItem}>
              <LocationOnRounded className={classes.footerItemIcon} />
              <Typography variant='caption' className={classes.footerItemText}>
                {activityLocation}
              </Typography>
            </Box>
            {/* <Box className={classes.footerItem}>
              <AccessTime className={classes.footerItemIcon} />
              <Typography variant='caption' className={classes.footerItemText}>
                {hasOpenRoster ? 'Open Roster' : 'Managed Roster'}
              </Typography>
            </Box> */}
            <Box className={classes.footerItem}>
              <Typography variant='h2' className={classes.footerItemLargeNumber}>
                {numSessions}
              </Typography>
              <Typography variant='caption' className={classes.footerItemText}>
                {numSessions > 1 ? 'sessions available' : 'session available'}
              </Typography>
            </Box>
            <Box className={classes.footerItem}>
              <Box display='flex' alignItems='center' pt={1}>
                <Explore style={{ color: theme.color.grey.neutral200, fontSize: 14, marginRight: '6px' }} />
                <Typography variant={'caption'} style={{ color: theme.color.grey.neutral200, marginTop: '1px' }}>
                  {programName}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </DateInfoCard>
    </ListItem>
  );
};
